import React, { useMemo, useState } from 'react';
import { TimeTrackerSummaryCharts } from './TimeTrackerSummaryCharts';
import { summarizeEntries, formatDuration, formatDecimalHours, formatCurrency, resolveRate, calculateAmount } from './timeTrackerUtils';
import { exportToCSV } from '@/utils/pmDataExport';
import type { TimeTrackerEntry, TimeTrackerProject, TimeTrackerClient, TimeTrackerTask } from '@/types/pm-types';
import { pmTheme } from './pmTheme';

interface TimeTrackerReportsViewProps {
  entries: TimeTrackerEntry[];
  projects: TimeTrackerProject[];
  clients: TimeTrackerClient[];
  tasks: TimeTrackerTask[];
  currency?: string;
  accentColor?: string;
}

type ReportTab = 'summary' | 'detailed';

const DATE_PRESETS = [
  { label: 'This Week', key: 'this-week' },
  { label: 'Last Week', key: 'last-week' },
  { label: 'This Month', key: 'this-month' },
  { label: 'Last Month', key: 'last-month' },
  { label: 'All Time', key: 'all' },
  { label: 'Custom', key: 'custom' },
] as const;

function getDateRange(preset: string): { start: string; end: string } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = today.getDay();

  switch (preset) {
    case 'this-week': {
      const monday = new Date(today);
      monday.setDate(today.getDate() - ((day + 6) % 7));
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return { start: monday.toISOString().slice(0, 10), end: sunday.toISOString().slice(0, 10) };
    }
    case 'last-week': {
      const monday = new Date(today);
      monday.setDate(today.getDate() - ((day + 6) % 7) - 7);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return { start: monday.toISOString().slice(0, 10), end: sunday.toISOString().slice(0, 10) };
    }
    case 'this-month': {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { start: first.toISOString().slice(0, 10), end: last.toISOString().slice(0, 10) };
    }
    case 'last-month': {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: first.toISOString().slice(0, 10), end: last.toISOString().slice(0, 10) };
    }
    default:
      return { start: '', end: '' };
  }
}

export const TimeTrackerReportsView: React.FC<TimeTrackerReportsViewProps> = ({
  entries,
  projects,
  clients,
  tasks,
  currency = 'USD',
  accentColor = '#10b981',
}) => {
  const [activeTab, setActiveTab] = useState<ReportTab>('summary');
  const [datePreset, setDatePreset] = useState('all');
  const [filterProjectId, setFilterProjectId] = useState('');
  const [sortField, setSortField] = useState<'date' | 'duration' | 'amount'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const dateRange = useMemo(() => {
    if (datePreset === 'custom') return { start: customStart, end: customEnd };
    return getDateRange(datePreset);
  }, [datePreset, customStart, customEnd]);

  const filteredEntries = useMemo(() => {
    let result = entries;
    if (dateRange.start) result = result.filter((e) => e.date >= dateRange.start);
    if (dateRange.end) result = result.filter((e) => e.date <= dateRange.end);
    if (filterProjectId) result = result.filter((e) => e.projectId === filterProjectId);
    return result;
  }, [entries, dateRange, filterProjectId]);

  const summary = useMemo(
    () => summarizeEntries(filteredEntries, projects, tasks),
    [filteredEntries, projects, tasks]
  );

  // For detailed view: sorted entries with amounts
  const detailedEntries = useMemo(() => {
    const withAmount = filteredEntries.map((e) => ({
      ...e,
      amount: e.billable ? calculateAmount(e.duration, resolveRate(projects, tasks, e.projectId, e.taskId)) : 0,
      projectName: projects.find((p) => p.id === e.projectId)?.name || '',
    }));

    withAmount.sort((a, b) => {
      if (sortField === 'date') return sortDir === 'desc' ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date);
      if (sortField === 'duration') return sortDir === 'desc' ? b.duration - a.duration : a.duration - b.duration;
      if (sortField === 'amount') return sortDir === 'desc' ? b.amount - a.amount : a.amount - b.amount;
      return 0;
    });

    return withAmount;
  }, [filteredEntries, projects, tasks, sortField, sortDir]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const handleExportCSV = () => {
    const fieldMapping: Record<string, string | undefined> = {
      date: 'date', description: 'description', projectName: 'projectName',
      duration: 'duration', amount: 'amount', billable: 'billable',
    };
    const data: Record<string, unknown>[] = detailedEntries.map((e) => ({
      date: e.date, description: e.description, projectName: e.projectName,
      duration: formatDecimalHours(e.duration), amount: e.amount.toFixed(2), billable: e.billable ? 'Yes' : 'No',
    }));
    exportToCSV(data, fieldMapping, ['date', 'description', 'projectName', 'duration', 'amount', 'billable'], 'time-report');
  };

  const avgDaily = useMemo(() => {
    const uniqueDates = new Set(filteredEntries.map((e) => e.date));
    return uniqueDates.size > 0 ? summary.totalSeconds / uniqueDates.size : 0;
  }, [filteredEntries, summary.totalSeconds]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Filter bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderBottom: `1px solid ${pmTheme.border}`, background: pmTheme.surfaceSubtle, flexWrap: 'wrap' }}>
        {DATE_PRESETS.map((preset) => (
          <button
            key={preset.key}
            onClick={() => setDatePreset(preset.key)}
            style={{
              padding: '4px 12px',
              border: datePreset === preset.key ? `1px solid ${accentColor}` : `1px solid ${pmTheme.input}`,
              borderRadius: 16,
              background: datePreset === preset.key ? accentColor + '15' : pmTheme.surface,
              color: datePreset === preset.key ? accentColor : pmTheme.textMuted,
              fontSize: 12,
              cursor: 'pointer',
              fontWeight: datePreset === preset.key ? 600 : 400,
            }}
          >
            {preset.label}
          </button>
        ))}
        {datePreset === 'custom' && (
          <>
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              style={{ padding: '4px 8px', border: `1px solid ${pmTheme.input}`, borderRadius: 6, fontSize: 12, color: pmTheme.text, background: pmTheme.surface }}
            />
            <span style={{ fontSize: 12, color: pmTheme.textMuted }}>to</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              style={{ padding: '4px 8px', border: `1px solid ${pmTheme.input}`, borderRadius: 6, fontSize: 12, color: pmTheme.text, background: pmTheme.surface }}
            />
          </>
        )}
        <select
          value={filterProjectId}
          onChange={(e) => setFilterProjectId(e.target.value)}
          style={{ padding: '4px 8px', border: `1px solid ${pmTheme.input}`, borderRadius: 6, fontSize: 12, marginLeft: 'auto', color: pmTheme.text, background: pmTheme.surface }}
        >
          <option value="">All Projects</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {/* Stats bar */}
      <div style={{ display: 'flex', gap: 16, padding: '12px 16px', borderBottom: `1px solid ${pmTheme.border}` }}>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: pmTheme.text }}>{formatDecimalHours(summary.totalSeconds)}</div>
          <div style={{ fontSize: 11, color: pmTheme.textSubtle }}>Total Hours</div>
        </div>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: accentColor }}>{formatDecimalHours(summary.billableSeconds)}</div>
          <div style={{ fontSize: 11, color: pmTheme.textSubtle }}>Billable Hours</div>
        </div>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: pmTheme.text }}>{formatCurrency(summary.totalBillable, currency)}</div>
          <div style={{ fontSize: 11, color: pmTheme.textSubtle }}>Total Revenue</div>
        </div>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: pmTheme.textMuted }}>{formatDecimalHours(avgDaily)}</div>
          <div style={{ fontSize: 11, color: pmTheme.textSubtle }}>Avg Daily</div>
        </div>
      </div>

      {/* Report tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${pmTheme.border}` }}>
        {(['summary', 'detailed'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '8px 20px',
              border: 'none',
              borderBottom: activeTab === tab ? `2px solid ${accentColor}` : '2px solid transparent',
              background: 'transparent',
              color: activeTab === tab ? accentColor : pmTheme.textMuted,
              fontWeight: activeTab === tab ? 600 : 400,
              fontSize: 13,
              cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {tab}
          </button>
        ))}
        {activeTab === 'detailed' && (
          <button
            onClick={handleExportCSV}
            style={{ marginLeft: 'auto', marginRight: 16, padding: '4px 12px', border: `1px solid ${pmTheme.input}`, borderRadius: 6, background: pmTheme.surface, fontSize: 12, cursor: 'pointer', color: pmTheme.text, alignSelf: 'center' }}
          >
            Export CSV
          </button>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {activeTab === 'summary' && (
          <TimeTrackerSummaryCharts
            entries={filteredEntries}
            projects={projects}
            accentColor={accentColor}
          />
        )}

        {activeTab === 'detailed' && (
          <div style={{ padding: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: pmTheme.surfaceSubtle, borderBottom: `1px solid ${pmTheme.border}` }}>
                  <th onClick={() => handleSort('date')} style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer', color: pmTheme.textMuted, fontWeight: 600, fontSize: 12 }}>
                    Date {sortField === 'date' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                  </th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', color: pmTheme.textMuted, fontWeight: 600, fontSize: 12 }}>Description</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', color: pmTheme.textMuted, fontWeight: 600, fontSize: 12 }}>Project</th>
                  <th onClick={() => handleSort('duration')} style={{ padding: '8px 12px', textAlign: 'right', cursor: 'pointer', color: pmTheme.textMuted, fontWeight: 600, fontSize: 12 }}>
                    Duration {sortField === 'duration' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                  </th>
                  <th onClick={() => handleSort('amount')} style={{ padding: '8px 12px', textAlign: 'right', cursor: 'pointer', color: pmTheme.textMuted, fontWeight: 600, fontSize: 12 }}>
                    Amount {sortField === 'amount' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                  </th>
                </tr>
              </thead>
              <tbody>
                {detailedEntries.map((entry) => (
                  <tr key={entry.id} style={{ borderBottom: `1px solid ${pmTheme.borderSubtle}` }}>
                    <td style={{ padding: '8px 12px', color: pmTheme.textMuted }}>{entry.date}</td>
                    <td style={{ padding: '8px 12px', color: pmTheme.text }}>{entry.description || '(no description)'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: projects.find((p) => p.id === entry.projectId)?.color || pmTheme.input, display: 'inline-block' }} />
                        {entry.projectName || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{formatDuration(entry.duration)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: entry.amount > 0 ? '#10b981' : pmTheme.textSubtle }}>
                      {entry.amount > 0 ? formatCurrency(entry.amount, currency) : '—'}
                    </td>
                  </tr>
                ))}
                {detailedEntries.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: 24, textAlign: 'center', color: pmTheme.textSubtle }}>No entries found for selected filters.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

TimeTrackerReportsView.displayName = 'TimeTrackerReportsView';
